import { formatUserMentionToken } from '@erp/shared-utils';

/**
 * Pure text handling behind the note composer's @mention autocomplete.
 *
 * While typing, a mention reads as `@Full Name` in the textarea. The composer
 * remembers which user each picked occurrence stands for, and where it sits.
 * On submit `serializeMentions` swaps those occurrences for `{user:<uuid>}`
 * tokens. Editing or deleting one drops that person only.
 */

export interface MentionCandidate {
  id: string;
  name: string;
  email?: string | null;
}

export interface ActiveMentionQuery {
  /** Index of the `@` that opened the query. */
  start: number;
  /** Text typed after the `@`, up to the caret. */
  query: string;
}

const MAX_QUERY_LENGTH = 50;
const WORD_CHAR = /[\p{L}\p{N}_]/u;
/** What can extend a name, so `@Ann-Marie` is not read as `@Ann`. */
const NAME_CONTINUATION = /[\p{L}\p{N}_-]/u;

/**
 * The `@query` the caret is currently inside, or null. An `@` only opens a
 * query at the start of the text or after whitespace, so an email address
 * does not pop the picker. A query ending in whitespace is closed too: that is
 * where the caret sits right after a pick, and reopening there would make the
 * next Enter pick again instead of posting.
 */
export function findActiveMentionQuery(
  text: string,
  caret: number,
): ActiveMentionQuery | null {
  const before = text.slice(0, caret);
  const start = before.lastIndexOf('@');
  if (start === -1) return null;
  const preceding = before[start - 1];
  if (preceding !== undefined && !/\s/.test(preceding)) return null;
  const query = before.slice(start + 1);
  if (
    query.length > MAX_QUERY_LENGTH ||
    /^\s|\s$/.test(query) ||
    /[\n@]/.test(query)
  ) {
    return null;
  }
  return { start, query };
}

/** Candidates whose name or email contains `query`, case-insensitively. */
export function filterMentionCandidates(
  candidates: readonly MentionCandidate[],
  query: string,
  limit = 8,
): MentionCandidate[] {
  const needle = query.trim().toLowerCase();
  return candidates
    .filter(
      (candidate) =>
        !needle ||
        candidate.name.toLowerCase().includes(needle) ||
        (candidate.email?.toLowerCase().includes(needle) ?? false),
    )
    .slice(0, limit);
}

/**
 * Replaces the active `@query` with `@Name` plus one separating space, and
 * returns the caret just past that space.
 */
export function insertMention(
  text: string,
  active: ActiveMentionQuery,
  caret: number,
  candidate: MentionCandidate,
): { text: string; caret: number } {
  const handle = `@${candidate.name}`;
  const rest = text.slice(caret);
  const separator = /^\s/.test(rest) ? '' : ' ';
  return {
    text: text.slice(0, active.start) + handle + separator + rest,
    caret: active.start + handle.length + 1,
  };
}

export type ComposerKeyAction =
  'highlight-next' | 'highlight-previous' | 'pick' | 'close-picker' | 'submit';

const PICKER_KEY_ACTIONS: Readonly<Record<string, ComposerKeyAction>> = {
  ArrowDown: 'highlight-next',
  ArrowUp: 'highlight-previous',
  Enter: 'pick',
  Tab: 'pick',
  Escape: 'close-picker',
};

/**
 * What a keypress in the composer should do, or null to let the textarea
 * handle it. While the picker is open it owns the arrows, Enter, Tab and
 * Escape; otherwise Enter posts and Shift+Enter falls through to a newline.
 * Keys pressed mid-IME-composition always fall through.
 */
export function composerKeyAction(
  key: string,
  state: { shiftKey: boolean; isComposing: boolean; pickerOpen: boolean },
): ComposerKeyAction | null {
  if (state.isComposing) return null;
  if (state.pickerOpen) return PICKER_KEY_ACTIONS[key] ?? null;
  return key === 'Enter' && !state.shiftKey ? 'submit' : null;
}

/** Moves a wrapping highlight through `count` options. */
export function stepHighlight(
  current: number,
  count: number,
  step: 1 | -1,
): number {
  return count === 0 ? 0 : (current + step + count) % count;
}

/** A picked mention anchored to the `@` of its own occurrence in the draft. */
export interface TrackedMention {
  id: string;
  name: string;
  start: number;
}

function handleOf(name: string): string {
  return `@${name}`;
}

/** The draft still contains this exact, unedited occurrence. */
function mentionStillIntact(text: string, mention: TrackedMention): boolean {
  const handle = handleOf(mention.name);
  if (text.slice(mention.start, mention.start + handle.length) !== handle) {
    return false;
  }
  const before = text[mention.start - 1];
  if (before !== undefined && WORD_CHAR.test(before)) return false;
  const after = text[mention.start + handle.length];
  return after === undefined || !NAME_CONTINUATION.test(after);
}

/**
 * Moves mentions to where they sit after a textarea edit that replaced
 * `previous[replaceStart, replaceEnd)` and produced `next`. A mention the
 * edit touched is dropped, so a deleted `@Ann` cannot leave its person
 * attached to a different `@Ann` that remains.
 */
export function retargetMentions(
  mentions: readonly TrackedMention[],
  previous: string,
  next: string,
  replaceStart: number,
  replaceEnd: number,
): TrackedMention[] {
  const removed = Math.max(0, replaceEnd - replaceStart);
  const inserted = next.length - (previous.length - removed);
  const delta = inserted - removed;
  const kept: TrackedMention[] = [];
  for (const mention of mentions) {
    const end = mention.start + handleOf(mention.name).length;
    if (end <= replaceStart) {
      kept.push(mention);
    } else if (mention.start >= replaceEnd) {
      kept.push({ ...mention, start: mention.start + delta });
    }
  }
  return kept.filter((mention) => mentionStillIntact(next, mention));
}

/** `retargetMentions`, plus the mention just inserted at `replaceStart`. */
export function placeMention(
  mentions: readonly TrackedMention[],
  previous: string,
  next: string,
  replaceStart: number,
  replaceEnd: number,
  candidate: MentionCandidate,
): TrackedMention[] {
  return [
    ...retargetMentions(mentions, previous, next, replaceStart, replaceEnd),
    { id: candidate.id, name: candidate.name, start: replaceStart },
  ].filter((mention) => mentionStillIntact(next, mention));
}

/** Drops mentions whose text is no longer where it was recorded. */
export function dropBrokenMentions(
  text: string,
  mentions: readonly TrackedMention[],
): TrackedMention[] {
  return mentions.filter((mention) => mentionStillIntact(text, mention));
}

/**
 * Turns each picked occurrence into its `{user:<uuid>}` token. An occurrence
 * the author deleted or edited is absent, so that person is not mentioned,
 * and an `@Name` they typed without picking is left as text. Trims the draft.
 */
export function serializeMentions(
  text: string,
  mentions: readonly TrackedMention[],
): string {
  const leading = text.length - text.trimStart().length;
  let body = text.trim();
  const kept = mentions
    .map((mention) => ({ ...mention, start: mention.start - leading }))
    .filter((mention) => mentionStillIntact(body, mention))
    .sort((a, b) => b.start - a.start);
  for (const mention of kept) {
    const handle = handleOf(mention.name);
    body =
      body.slice(0, mention.start) +
      formatUserMentionToken(mention.id) +
      body.slice(mention.start + handle.length);
  }
  return body;
}
